// Sesión de la SPA (`GET /api/v1/session`, API §3): usuario, negocio, módulos, locales y políticas.
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionLocation, SessionResponse } from '@atte/contracts';
import { api, isApiError } from './api';
import { qk } from './queryKeys';
import { setServerNow } from './time';

export type Session = SessionResponse;
export type { SessionLocation };

/** Tiempo máximo para el arranque antes de mostrar el error (UX §15.1). */
export const BOOT_TIMEOUT_MS = 15_000;

export const sessionQuery = queryOptions({
  queryKey: qk.session,
  queryFn: async ({ signal }) => {
    const s = await api.get<SessionResponse>('/session', { signal, skipGlobalHandlers: true, timeoutMs: BOOT_TIMEOUT_MS });
    setServerNow(s.serverNow);
    return s;
  },
  staleTime: 5 * 60_000,
  // Sin sesión no se reintenta; red y 5xx sí (el arranque reintenta además cada 10 s).
  retry: (n, e) => n < 1 && isApiError(e) && e.isTransient,
  refetchOnWindowFocus: true,
});

/** Consulta de la sesión con estados (para guardas y arranque). */
export function useSessionQuery() {
  return useQuery(sessionQuery);
}

/**
 * Sesión ya cargada. Solo dentro de `RequireSession` (si no, lanza): las pantallas internas nunca ven la
 * sesión vacía.
 */
export function useSession(): Session {
  const { data } = useQuery(sessionQuery);
  if (!data) throw new Error('useSession() fuera de RequireSession');
  return data;
}

/** Sesión si existe (pantallas públicas o de ingreso). */
export function useOptionalSession(): Session | undefined {
  return useQuery({ ...sessionQuery, enabled: false }).data;
}

/** Vuelve a pedir la sesión (después de cambiar contraseña, 2FA, módulos…). */
export function useRefreshSession() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.session });
}

/** Vocabulario del rubro (D-04): `{ staff, Staff, client, ClientPl, … }`. */
export function useVocab(): Session['business']['vocab'] {
  return useSession().business.vocab;
}

/** Local por id dentro de la sesión. */
export function findLocation(session: Session, idOrSlug: string | null | undefined): SessionLocation | undefined {
  if (!idOrSlug) return undefined;
  return session.locations.find((l) => l.id === idOrSlug || l.slug === idOrSlug);
}

/** Local de entrada: el último abierto (perfil) o el primero (UX §2.4, UX-P51). */
export function defaultLocation(session: Session): SessionLocation | undefined {
  return findLocation(session, session.user.lastLocationId) ?? session.locations[0];
}

/**
 * Adónde ir después de ingresar o al abrir `/` con sesión (UX §2.3, §3.1): restricciones primero, después
 * `volver` (si es una ruta interna válida), "Primeros pasos" del dueño y por último el último local.
 */
export function homePathFor(session: Session, returnTo?: string | null): string {
  if (session.restrictions.passwordChangeRequired) return '/primer-acceso';
  if (session.restrictions.mfaSetupRequired) return '/configurar-2fa';
  if (session.locations.length === 0) return '/sin-acceso';
  if (returnTo && isSafeReturnPath(returnTo)) return returnTo;
  if (session.user.role === 'owner' && !session.business.setupCompleted) return '/primeros-pasos';
  const loc = defaultLocation(session);
  return loc ? `/app/${loc.slug}/agenda` : '/sin-acceso';
}

/** Solo rutas internas de la app (evita redirecciones abiertas con `?volver=`). */
export function isSafeReturnPath(path: string): boolean {
  return /^\/(app|primeros-pasos)(\/|$|\?)/.test(path) && !path.startsWith('//');
}
